import { Typography } from '@telavita-core/react-design-kit'
import React, { useCallback, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import MedicalRecordsService from 'services/medicalRecords'
import { MedicalRecordFormData } from '../../medicalRecordSchema'
import { Option, OTHER_MEDICATION_OPTION_ID, NONE_MEDICATION_OPTION_ID } from '../../types/option'
import { OptionDetailsSelect } from '../OptionDetailsSelect'
import { OtherOptionInput } from '../OtherOptionInput'
import { SearchMultiSelect } from '../SearchMultiSelect'
import { useMedicalRecordForm } from '../../hooks/useMedicalRecordForm'

import * as S from './styles'

interface SelectStatusOption {
  id: string
  label: string
}

const typesStatusOptions: SelectStatusOption[] = [
  { id: 'IN_USE', label: 'Em uso' },
  { id: 'INTERRUPTED', label: 'Interrompido' },
]

interface MedicationsOption extends Option {
  id: number
  commercialName: string
  description: string
  status: SelectStatusOption
  medication_medical_record_form_id: number
}

const OTHERS_LABEL = 'Outros(as)'

export function MedicationsSelect(): JSX.Element {
  const [medicationsOptions, setMedicationsOptions] = useState([])

  const {
    control,
    watch,
    setValue,
    resetField,
    formState: { errors },
  } = useFormContext<MedicalRecordFormData>()
  const medicationsSelected = watch('medications') || []
  const hasMedicationsSelected = medicationsSelected.length > 0
  const { form } = useMedicalRecordForm()
  const handleChangeValue = (value: MedicationsOption[]) =>
    setValue('medications', value)
  const lastOtherMedication = form?.other_medications?.[0]
  const hasOtherOptionInSelectedOptions = (option: MedicationsOption) =>
    option.commercialName === OTHERS_LABEL

  const hasOtherOrNoneOptionInOptions = ((optionId: number) =>
    [OTHER_MEDICATION_OPTION_ID, NONE_MEDICATION_OPTION_ID].includes(optionId))

  function handleRemoveSelectedOption(optionId: number) {
    const options = medicationsSelected.filter((op) => op.id !== optionId)
    if (optionId === OTHER_MEDICATION_OPTION_ID) {
      resetField('other_medication')
    }

    handleChangeValue(options)
  }

  const getMedications = useCallback(
    async (query: string, page: number, isSearch = false) => {
      const result = await MedicalRecordsService.listMedications(query, page)
      const options = result.medications.map((medication) => {
        const name = hasOtherOrNoneOptionInOptions(medication.id)
          ? medication.commercialName
          : `${medication.commercialName} (${medication.genericName})`

        return {
          id: medication.id,
          description: medication.genericName,
          commercialName: medication.commercialName,
          name: name,
          status: undefined,
        } as MedicationsOption
      })

      setMedicationsOptions((state) => {
        if (isSearch) return options

        return [...state, ...options]
      })

      return result
    },
    []
  )

  const isOptionNone = (option: Option) => option.id === NONE_MEDICATION_OPTION_ID

  return (
    <S.MedicationsSelectContainer>
      <Controller
        control={control}
        name='medications'
        render={({ field }) => (
          <SearchMultiSelect
            {...field}
            selectedOptions={medicationsSelected}
            onChange={field.onChange}
            options={medicationsOptions}
            label='Medicação de uso contínuo *'
            id='medication-select'
            description='Se não houver, digite e/ou selecione “nenhuma” para adicionar. Caso as
        opções não atendam, escolha “outros”.'
            floatingLabel='Digite e/ou selecione para adicionar'
            inputPlaceholder='Digite e/ou selecione para adicionar'
            emptyOptionsMessage='Nenhum resultado. Verifique sua digitação.'
            errorMessage={errors.medications?.message}
            loadOptions={getMedications}
          />
        )}
      />
      {hasMedicationsSelected && (
        <S.SelectedOptionsContainer>
          <S.SelectedOptionsTitle>
            <Typography variant='content3' italic>
              Adicionadas até agora:
            </Typography>
            <hr />
          </S.SelectedOptionsTitle>
          {medicationsSelected.map((option, index) => {
            const hasOtherOption = hasOtherOptionInSelectedOptions(option)
            const isOptionWithoutId = !option.medication_medical_record_form_id
            const isOptionWithOtherInherited = Boolean((hasOtherOption && lastOtherMedication?.id) && option.id === OTHER_MEDICATION_OPTION_ID)
            const shouldEnableRemoveButton = isOptionWithoutId && !isOptionWithOtherInherited
            return (
              <Controller
                key={option.id}
                control={control}
                name={`medications.${index}.status`}
                render={({ field, fieldState: { error } }) => (
                  <OptionDetailsSelect
                    {...field}
                    id={option.id}
                    nameId='medication-select-option'
                    options={typesStatusOptions}
                    onChange={(value) => field.onChange(value)}
                    messageError={error?.message}
                    noStatus={isOptionNone(option)}
                    otherInput={
                      hasOtherOption && (
                        <Controller
                          control={control}
                          name='other_medication'
                          render={({ field, fieldState: { error } }) => (
                            <OtherOptionInput
                              {...field}
                              errorMessage={error?.message}
                              placeholder='Qual?'
                              disabled={isOptionWithOtherInherited}
                            />
                          )}
                        />
                      )
                    }
                    name={!hasOtherOption && option.commercialName}
                    onRemoveOption={shouldEnableRemoveButton && handleRemoveSelectedOption}
                    placeholder='Status'
                  />
                )}
              />
            )
          })}
        </S.SelectedOptionsContainer>
      )}
    </S.MedicationsSelectContainer>
  )
}
